import { useEffect, useRef } from 'react';
import { MODULES, PLAN_LABELS, type ModuleKey, type Plan } from '@kontor/shared';
import { useSession } from '../session';

/**
 * Hinweisfenster, wenn ein Mandant eine Funktion anklickt, deren Modul sein Paket nicht enthält (KON-082). Es sagt, was das Modul bringt,
 * in welchem Paket es steckt und welches Paket der Mandant hat. Gedacht für jedes Modul, nicht nur für Angebote. Es ist ein echtes
 * Dialogfenster: Der Fokus bleibt darin, Escape und ein Klick daneben schließen es, der Fokus kehrt zur Schaltfläche zurück.
 */
export function UpgradeDialog({ module, open, onClose }: { module: ModuleKey; open: boolean; onClose: () => void }) {
  const { session } = useSession();
  const dialog = useRef<HTMLDialogElement>(null);
  const definition = MODULES.find((m) => m.key === module);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  if (!definition) return null;
  const current = PLAN_LABELS[(session?.tenant.plan ?? 'basic') as Plan] ?? session?.tenant.plan ?? '';
  const needed = definition.plan ? PLAN_LABELS[definition.plan] : null;

  return (
    <dialog
      ref={dialog}
      className="dialog"
      aria-labelledby="upgrade-title"
      onClose={onClose}
      // Ein Klick auf den abgedunkelten Rand (das Dialogelement selbst) schließt das Fenster
      onClick={(e) => {
        if (e.target === dialog.current) onClose();
      }}
    >
      <div className="dialog__body">
        <h2 id="upgrade-title">„{definition.label}“ ist nicht in Ihrem Paket enthalten</h2>
        <p>{definition.description}.</p>
        <dl className="facts dialog__facts">
          <div>
            <dt>Ihr Paket</dt>
            <dd>{current}</dd>
          </div>
          <div>
            <dt>{needed ? 'Enthalten ab Paket' : 'Erhältlich als'}</dt>
            <dd>{needed ?? 'Zusatzmodul'}</dd>
          </div>
        </dl>
        <p>
          {needed ? `Mit dem Paket ${needed}` : 'Als Zusatzmodul'} können Sie {definition.label} nutzen. Sprechen Sie uns an, wir stellen Ihr Paket gern um. Ihre bisherigen Daten bleiben dabei unverändert.
        </p>
        <div className="form-actions">
          <button type="button" className="btn btn--primary" onClick={onClose} autoFocus>Verstanden</button>
        </div>
      </div>
    </dialog>
  );
}
